import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {LabAutomationAllSchemasQuery} from 'lab-automation/graphql/__generated/LabAutomationAllSchemasQuery';
import {LAB_AUTOMATION_ALL_SCHEMAS_QUERY} from 'lab-automation/graphql/AllSchemas';
import {LookupConfigContext} from 'lab-automation/settings/automation-input-file-config/LookupConfigContext';
import {LookupConfigDescription} from 'lookup-config/LookupConfigDescription';
import {
  DEFAULT_FINAL_LOOKUP_STEP_TYPES,
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  FIRST_STEP_OPTIONS_FOR_COLUMNS_MAP,
  LookupConfigSpec,
} from 'lookup-config/LookupConfigModalConstants';
import {lookupStepsToInfosArray} from 'lookup-config/LookupStepInitializerUtils';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  LookupConfigInfoTypedValue,
  LookupConfigTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import runLookupConfigModalForLabAutomation from '../runLookupConfigModalForLabAutomation';

export interface LookupConfigCellDropdownProps {
  isSelected: boolean;
  locked: boolean;
  typedValue: LookupConfigInfoTypedValue;
  onOpenDropdown: () => void;
  onUpdate: (value: LookupConfigInfoTypedValue) => void;
  fullWidth?: boolean;
}

export const DATA_TEST_ATTRS = {
  setLookup: {component: 'LookupConfigCellDropdown', element: 'setLookup'},
  editLookup: {component: 'LookupConfigCellDropdown', element: 'editLookup'},
  removeLookup: {component: 'LookupConfigCellDropdown', element: 'removeLookup'},
};

export default function LookupConfigCellDropdown(props: LookupConfigCellDropdownProps): JSX.Element {
  const {baseSchema} = LookupConfigContext.useContext();
  const [isOpen, setOpen] = useState(false);
  useEffect(() => {
    if (!props.isSelected) {
      setOpen(false);
    }
  }, [props.isSelected]);

  // All schemas need to be loaded before we can call lookupStepsToInfosArray
  const allSchemasQueryResult = useBenchlingQuery<LabAutomationAllSchemasQuery>(
    LAB_AUTOMATION_ALL_SCHEMAS_QUERY
  );
  if (allSchemasQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  const _toLookupConfigSpec = (lookup: LookupConfigTypedValue | null): LookupConfigSpec | null =>
    lookup != null ? (lookup as LookupConfigSpec) : null;

  const lookupConfig = _toLookupConfigSpec(props.typedValue.lookupConfig);
  const source = _toLookupConfigSpec(props.typedValue.source);
  const destination = _toLookupConfigSpec(props.typedValue.destination);
  const sourceLookupInfos = source ? lookupStepsToInfosArray(source.lookupSteps, baseSchema, {}) : null;
  const destinationLookupInfos = destination
    ? lookupStepsToInfosArray(destination.lookupSteps, baseSchema, {})
    : null;

  function _handleOpenMenu(): void {
    props.onOpenDropdown();
    setOpen(true);
  }

  let fullWidthClickHandler;
  if (props.fullWidth) {
    fullWidthClickHandler = _handleOpenMenu;
  }

  const display =
    lookupConfig != null ? (
      <LookupConfigDescription
        baseSchema={baseSchema}
        lookupConfig={lookupConfig}
        context={{sourceLookupInfos, destinationLookupInfos}}
      />
    ) : (
      'Set lookup'
    );

  const renderOptions = (): JSX.Element => {
    if (lookupConfig == null) {
      return (
        <li key="set-lookup" className={classNames({disabled: props.locked})}>
          <a
            onClick={() => {
              runLookupConfigModalForLabAutomation({
                baseSchema,
                firstLookupStepTypes: FIRST_STEP_OPTIONS_FOR_COLUMNS_MAP,
                intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
                finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
                shouldShowIsMulti: true,
                context: {
                  sourceLookupInfos,
                  destinationLookupInfos,
                  usePlateLayout: props.typedValue.usePlateLayout,
                },
                onRequestSubmit: ([{lookupConfig}]) => {
                  props.onUpdate({
                    type: BaseTypedValueType.LOOKUP_CONFIG_INFO,
                    lookupConfig: lookupConfig as LookupConfigTypedValue,
                    source: props.typedValue.source,
                    destination: props.typedValue.destination,
                    usePlateLayout: props.typedValue.usePlateLayout,
                  });
                  setOpen(false);
                },
              });
            }}
            {...getTestProps(DATA_TEST_ATTRS.setLookup)}
          >
            Set lookup
          </a>
        </li>
      );
    }
    return (
      <>
        <li key="edit-lookup" className={classNames({disabled: props.locked})}>
          <a
            onClick={() => {
              runLookupConfigModalForLabAutomation({
                baseSchema,
                firstLookupStepTypes: FIRST_STEP_OPTIONS_FOR_COLUMNS_MAP,
                intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
                finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
                initialLookupConfig: lookupConfig,
                shouldShowIsMulti: true,
                context: {
                  sourceLookupInfos,
                  destinationLookupInfos,
                  usePlateLayout: props.typedValue.usePlateLayout,
                },
                onRequestSubmit: ([{lookupConfig}]) => {
                  props.onUpdate({
                    type: BaseTypedValueType.LOOKUP_CONFIG_INFO,
                    lookupConfig: lookupConfig as LookupConfigTypedValue,
                    source: props.typedValue.source,
                    destination: props.typedValue.destination,
                    usePlateLayout: props.typedValue.usePlateLayout,
                  });
                  setOpen(false);
                },
              });
            }}
            {...getTestProps(DATA_TEST_ATTRS.editLookup)}
          >
            Edit lookup
          </a>
        </li>
        <li key="remove-lookup" className={classNames({disabled: props.locked})}>
          <a
            onClick={() => {
              props.onUpdate({
                type: BaseTypedValueType.LOOKUP_CONFIG_INFO,
                lookupConfig: null,
                source: props.typedValue.source,
                destination: props.typedValue.destination,
              });
              setOpen(false);
            }}
            {...getTestProps(DATA_TEST_ATTRS.removeLookup)}
          >
            Remove lookup
          </a>
        </li>
      </>
    );
  };
  return (
    <div
      className={classNames(css(styles.lookupCell), 'u-pointer', 'u-flex-display')}
      onClick={fullWidthClickHandler}
    >
      <span className={classNames(css(styles.lookupCellContent), 'u-flex-grow')}>{display}</span>
      <div className="selectorDropdown">
        <AbsoluteDropdownContainer
          onRequestClose={() => {
            setOpen(false);
          }}
          open={isOpen}
          shouldCloseOnInsideClick={true}
          menu={
            <ul className="selectorDropdown-list dropdown-menu scrollable pull-right">{renderOptions()}</ul>
          }
        >
          <BenchlingIcon
            className={classNames('selectorDropdown-button', {'is-open': isOpen})}
            icon="chevron-down"
            onClick={_handleOpenMenu}
          />
        </AbsoluteDropdownContainer>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  lookupCell: {
    marginRight: '15px',
  },
  lookupCellContent: {
    width: '100%',
  },
});
